import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

const faqs = [
  {
    category: 'For Pet Owners',
    color: 'text-brand-teal',
    bg: 'bg-teal-50',
    items: [
      {
        q: 'Is PetCares free to use for pet owners?',
        a: 'Yes — completely free. PetCares will never charge pet owners for finding a shop or a vet. You browse, you call, and you deal directly with the business. There are no platform fees, no booking commissions, and no hidden charges.',
      },
      {
        q: 'How do I know the pet shops are trustworthy?',
        a: 'Every pet shop listed on PetCares has been personally visited or contacted by our team before listing. We verify that they actively offer boarding or daycare services, confirm their operating hours, and collect photos of the facility. Shops that do not meet our standards are not listed.',
      },
      {
        q: 'What if I need a vet at midnight or on a Sunday?',
        a: 'The Emergency section specifically lists vets who are available outside standard clinic hours. Each vet\'s availability is shown clearly on their card. Several Coimbatore vets on our platform are available 24/7 or until late at night specifically to handle emergencies.',
      },
      {
        q: 'My pet is not a dog or cat. Can PetCares still help?',
        a: 'Yes. We have listed pet shops and vets who specifically handle birds, rabbits, reptiles, aquarium fish, and other exotic animals. Use the service tags to filter. If you cannot find someone for your pet type, call us and we will personally help you locate a specialist.',
      },
      {
        q: 'Can I trust the phone numbers listed?',
        a: 'Every phone number on PetCares has been called and verified by our team before the listing goes live. We re-verify periodically. If you ever find a number that does not connect, contact us and we will fix it within 24 hours.',
      },
    ],
  },
  {
    category: 'For Pet Shop & Boarding Owners',
    color: 'text-brand-orange',
    bg: 'bg-orange-50',
    items: [
      {
        q: 'Do you charge commission on bookings made through PetCares?',
        a: 'Never. PetCares is a discovery platform — we help pet owners find you, and you take it from there. All communication and payment happens directly between you and the customer. We do not take any cut. Our platform is sustained through future premium features for businesses, not commissions on your earnings.',
      },
      {
        q: 'What if I want to update my services, hours, or contact details?',
        a: 'Just call or WhatsApp our support number. We handle all listing updates for you. There is no dashboard to log in to, no forms to fill — you tell us the change, we update your listing within 24 hours.',
      },
      {
        q: 'How long does it take to get listed?',
        a: 'Typically 1–2 working days. After you contact us, we do a quick verification call, write your listing content, send it to you for approval via WhatsApp, and go live once you confirm. The entire process is handled by our team — you just approve the final version.',
      },
      {
        q: 'What kind of pet shops can list on PetCares?',
        a: 'Any legitimate, locally-operating pet shop that offers at least one of: pet boarding, pet daycare, dog walking, grooming, or pet sitting. We also accept shops that sell pet supplies if they also provide any care service. We do not list online-only businesses.',
      },
    ],
  },
  {
    category: 'For Veterinarians',
    color: 'text-emergency-red',
    bg: 'bg-red-50',
    items: [
      {
        q: 'What documents do I need to register as a vet?',
        a: 'We verify your State Veterinary Council registration number and your clinic address. You do not need to send us physical documents — our team will call to confirm the details and cross-check with the council register. The process is straightforward and takes under 30 minutes.',
      },
      {
        q: 'Will I get calls at all hours? I am worried about load.',
        a: 'Your listing shows your actual availability. If you are available only until 10 PM, we list you accordingly. PetCares does not override your listed hours. Owners searching for midnight emergencies will only see vets who have indicated 24/7 availability. You are in control of when you appear in searches.',
      },
      {
        q: 'Can I list my specialty area so I get the right cases?',
        a: 'Yes — this is one of the core features. Your listing includes your specialty tags: emergency surgery, critical care, dermatology, exotics, dentistry, and more. Pet owners searching for an emergency vet for a rabbit will see exotic specialists first. You receive calls matched to what you actually do.',
      },
      {
        q: 'What does PetCares charge for its vet listing service?',
        a: 'Nothing. Veterinary listings on PetCares are completely free. We believe connecting emergency vets to pet owners in crisis is a public good — not a revenue line. If and when we introduce premium features (e.g. verified badges, priority placement), they will be clearly optional.',
      },
    ],
  },
];

const FAQItem = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-gray-100 rounded-2xl overflow-hidden bg-white hover:shadow-sm transition-all">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex justify-between items-start gap-4 px-6 py-5 text-left"
      >
        <span className="font-semibold text-gray-900 text-[15px] leading-snug">{q}</span>
        {open ? <ChevronUp size={20} className="text-gray-400 flex-shrink-0 mt-0.5" /> : <ChevronDown size={20} className="text-gray-400 flex-shrink-0 mt-0.5" />}
      </button>
      {open && (
        <div className="px-6 pb-5">
          <p className="text-gray-500 text-[14px] leading-relaxed border-t border-gray-50 pt-4">{a}</p>
        </div>
      )}
    </div>
  );
};

const FAQ = () => {
  return (
    <section id="faq" className="py-24 bg-white border-t border-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block text-brand-teal font-bold tracking-widest uppercase text-xs mb-3 bg-teal-50 px-4 py-1.5 rounded-full border border-teal-100">
            Frequently Asked Questions
          </span>
          <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-4">
            Every Question,<br/>
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-brand-teal to-brand-orange">
              Answered Honestly
            </span>
          </h2>
          <p className="text-xl text-gray-500">
            Questions from pet owners, shop owners, and veterinarians — all addressed here.
          </p>
        </div>

        <div className="space-y-12">
          {faqs.map((group, gi) => (
            <div key={gi}>
              <div className={`inline-flex items-center gap-2 ${group.bg} ${group.color} text-sm font-bold px-4 py-2 rounded-full mb-5 border border-current border-opacity-20`}>
                {group.category}
              </div>
              <div className="space-y-3">
                {group.items.map((item, ii) => (
                  <FAQItem key={ii} q={item.q} a={item.a} />
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Closing note */}
        <div className="mt-14 text-center bg-gray-50 rounded-3xl p-8 border border-gray-100">
          <p className="text-gray-700 font-semibold text-lg mb-2">Have a question not listed here?</p>
          <p className="text-gray-500 mb-5">We respond to every inquiry within a few hours during business hours.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a href="tel:+919842111290" className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-brand-dark text-white font-bold rounded-full hover:bg-gray-800 transition">
              Call Us: +91 98421 11290
            </a>
            <a href="mailto:hello@petcares.in" className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white text-gray-800 font-bold rounded-full border border-gray-200 hover:bg-gray-50 transition">
              hello@petcares.in
            </a>
          </div>
        </div>

      </div>
    </section>
  );
};

export default FAQ;
